import { Dialog, DialogContent, DialogFooter } from "@/ui-components/ui/dialog";
import { StandardDialogHeader } from "@/util-components/dialog/components/StandardDialogHeader";
import { Button } from "@/ui-components/ui/button";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { Loader2 } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export interface CreateBaseDialogProps {
  /** Determines if the dialog is visible. */
  open: boolean;
  /** Callback triggered when the dialog wants to close. `refetchData` indicates if a reload is necessary. */
  onClose: (refetchData: boolean) => void;

  /** The Lucide icon to display in the header. */
  icon: any;
  /** The main title of the dialog. */
  title: ReactNode;
  /** A short description shown below the title. */
  description: ReactNode;

  /** The content of the dialog (usually the form). */
  children: ReactNode;

  /** Indicates if a save/submit operation is currently running. Disables buttons and shows a loader. */
  isSaving?: boolean;
  /** Indicates if the form is valid and can be submitted. */
  canSave?: boolean;
  /** The ID of the form element to connect the submit button with the form outside of the footer. */
  formId?: string;
  /** The label for the submit button. Defaults to "Erstellen". */
  submitLabel?: string;
  /** The label for the submit button while saving. Defaults to "Erstellen...". */
  submitLoadingLabel?: string;

  /** Custom action buttons to display in the footer, usually placed before the submit button. */
  customFooterActions?: ReactNode;
  /** Whether to hide the default submit button */
  hideSubmitButton?: boolean;

  /** The maximum width of the dialog. Defaults to "2xl". */
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl";
  /** Optional additional CSS classes for the DialogContent. */
  className?: string;
}

/**
 * A standardized base dialog component for creating entities.
 * Provides a consistent layout with a header, scrollable content area, and a fixed footer with standard action buttons.
 * Integrates permission checks for the submit action automatically.
 */
export const CreateBaseDialog = ({
  open,
  onClose,
  icon,
  title,
  description,
  children,
  isSaving = false,
  canSave = true,
  formId,
  submitLabel = "Erstellen",
  submitLoadingLabel = "Erstellen...",
  customFooterActions,
  hideSubmitButton = false,
  maxWidth = "2xl",
  className,
}: CreateBaseDialogProps) => {
  const maxWidthClass = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
  }[maxWidth];

  const hasRightSideActions = !!customFooterActions || !hideSubmitButton;

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose(false)}>
      <DialogContent
        className={cn(
          `${maxWidthClass} p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0`,
          className,
        )}
      >
        <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
          <StandardDialogHeader
            icon={icon}
            title={title}
            description={description}
            className="p-0 border-none"
          />
        </div>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {children}
        </div>

        <DialogFooter className="px-5 sm:px-6 py-4 shrink-0 border-t border-gray-100 bg-gradient-to-t from-background via-background to-background/95 !flex-col gap-3 sm:!flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex w-full sm:w-auto">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onClose(false)}
              disabled={isSaving}
              className="h-11 w-full justify-center rounded-xl px-4 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900 sm:w-auto sm:justify-start"
            >
              Abbrechen
            </Button>
          </div>
          {hasRightSideActions && (
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              {customFooterActions}
              {!hideSubmitButton && (
                <PermissionFeatureWrapper
                  requiredPermission={Permission.MANAGE_ENTITIES}
                  requiredPlan={PlanKey.BASIC}
                >
                  <Button
                    type="submit"
                    form={formId}
                    disabled={!canSave || isSaving}
                    className="h-11 w-full px-6 sm:px-8 min-w-[140px] rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all sm:w-auto"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {submitLoadingLabel}
                      </>
                    ) : (
                      submitLabel
                    )}
                  </Button>
                </PermissionFeatureWrapper>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
